// src/pages/PlansSubscription.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "../axiosConfig";
import "../styles/PlansSubscription.css";

export default function PlansSubscription() {
  const { t } = useTranslation("plans_subscription");

  // ---- Mock summary (kept for trailers/drivers until wired) ----
  const [cycle, setCycle] = useState("monthly");

  // Live TRUCKS count (used)
  const [truckCount, setTruckCount] = useState(() => {
    const c = readJSON(TRUCK_COUNT_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < TRUCK_COUNT_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live TRAILERS count (used)
  const [trailerCount, setTrailerCount] = useState(() => {
    const c = readJSON(TRAILER_COUNT_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < TRAILER_COUNT_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live USERS count (used) from /companies/users/count
  const [userCount, setUserCount] = useState(() => {
    const c = readJSON(USER_COUNT_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < USER_COUNT_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live DRIVERS count (used)
  const [driverCount, setDriverCount] = useState(() => {
    const c = readJSON(DRIVER_COUNT_CACHE_KEY);
    const fresh =
      c &&
      typeof c.v === "number" &&
      typeof c.ts === "number" &&
      Date.now() - c.ts < DRIVER_COUNT_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live TRUCKS limit
  const [maxTrucks, setMaxTrucks] = useState(() => {
    const c = readJSON(MAX_TRUCKS_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < MAX_TRUCKS_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live TRAILERS limit
  const [maxTrailers, setMaxTrailers] = useState(() => {
    const c = readJSON(MAX_TRAILERS_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < MAX_TRAILERS_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live DRIVERS limit (max_drivers) from /companies/current (cached)
  const [maxDrivers, setMaxDrivers] = useState(() => {
    const c = readJSON(MAX_DRIVERS_CACHE_KEY);
    const fresh =
      c &&
      typeof c.v === "number" &&
      typeof c.ts === "number" &&
      Date.now() - c.ts < MAX_DRIVERS_TTL_MS;
    return fresh ? c.v : null;
  });

  // Live USERS limit (max_users) from /companies/current (cached)
  const [maxUsers, setMaxUsers] = useState(() => {
    const c = readJSON(MAX_USERS_CACHE_KEY);
    const fresh = c && typeof c.v === "number" && typeof c.ts === "number" && (Date.now() - c.ts) < MAX_USERS_TTL_MS;
    return fresh ? c.v : null;
  });

  // Real prices for the *current* plan (euros from DB → we convert to cents in UI)
  const [planPrices, setPlanPrices] = useState(() => {
    const c = readJSON(PLAN_PRICES_CACHE_KEY);
    const fresh = c && c.v && typeof c.ts === "number" && (Date.now() - c.ts) < PLAN_PRICES_TTL_MS;
    return fresh ? c.v : { plan: null, monthly_price: null, yearly_price: null };
  });

  const CPE_CACHE_KEY = "stats.currentPeriodEnd";
  const CPE_TTL_MS    = 60 * 60_000; // 1h

  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(() => {
    const c = readJSON(CPE_CACHE_KEY);
    const fresh = c && typeof c.v === "string" && typeof c.ts === "number" && (Date.now() - c.ts) < CPE_TTL_MS;
    return fresh ? c.v : null; // ISO string
  });
  const [planStatus, setPlanStatus]   = useState(null); // "active" | "past_due" | "canceled" | ...
  const [autoRenews, setAutoRenews]   = useState(null); // boolean
  const [graceDays, setGraceDays]     = useState(null); // number

  // Live plan (used to derive default limit when DB max_trucks is missing)
  const [plan, setPlan] = useState(() => {
    const c = readJSON(PLAN_CACHE_KEY);
    const fresh = c && typeof c.v === "string" && typeof c.ts === "number" && (Date.now() - c.ts) < PLAN_TTL_MS;
    return fresh ? c.v : null;
  });

  // Current subscribed billing cycle (monthly | yearly)
  const BILLING_CYCLE_CACHE_KEY = "stats.billingCycle";
  const BILLING_CYCLE_TTL_MS    = 5 * 60_000;

  const [billingCycle, setBillingCycle] = useState(() => {
    const c = readJSON(BILLING_CYCLE_CACHE_KEY);
    const fresh = c && typeof c.v === "string" && typeof c.ts === "number" && (Date.now() - c.ts) < BILLING_CYCLE_TTL_MS;
    return fresh ? c.v : null;
  });

  // Normalize helper
  const norm = (v) => (typeof v === "string" ? v.toLowerCase() : v);

  // The values we actually display as the limits:
  const displayMaxTrucks = Number.isFinite(maxTrucks)
    ? maxTrucks
    : (PLAN_LIMITS[norm(plan)] ?? null);

  const displayMaxTrailers = Number.isFinite(maxTrailers)
    ? maxTrailers
    : (PLAN_LIMITS[norm(plan)] ?? null);

  const displayMaxUsers = Number.isFinite(maxUsers)
    ? maxUsers
    : (PLAN_LIMITS_USERS[norm(plan)] ?? null);

  // Limite de DRIVERS: usa max_drivers do Company; se faltar, cai no mapa por plano
  const displayMaxDrivers = Number.isFinite(maxDrivers)
    ? maxDrivers
    : (PLAN_LIMITS[norm(plan)] ?? null);

  // 👉 Usage só fica "pronto" quando temos todos os números necessários
  const usageReady =
  Number.isFinite(truckCount) &&
  Number.isFinite(displayMaxTrucks) &&
  Number.isFinite(trailerCount) &&
  Number.isFinite(displayMaxTrailers) &&
  Number.isFinite(userCount) &&
  Number.isFinite(displayMaxUsers) &&
  Number.isFinite(driverCount) &&
  Number.isFinite(displayMaxDrivers);

  useEffect(() => {
    let alive = true;

    // 1) Used trucks
    (async () => {
      try {
        if (truckCount == null) {
          const { data } = await api.get("/companies/trucks/count");
          if (alive && typeof data?.count === "number") {
            setTruckCount(data.count);
            writeJSON(TRUCK_COUNT_CACHE_KEY, { v: data.count, ts: Date.now() });
          }
        }
      } catch {}
    })();

    // 2) Used trailers
    (async () => {
      try {
        if (trailerCount == null) {
          const { data } = await api.get("/companies/trailers/count");
          if (alive && typeof data?.count === "number") {
            setTrailerCount(data.count);
            writeJSON(TRAILER_COUNT_CACHE_KEY, { v: data.count, ts: Date.now() });
          }
        }
      } catch {}
    })();

    // 3) Used users
    (async () => {
      try {
        if (userCount == null) {
          const { data } = await api.get("/companies/users/count"); // counts all users by default
          if (alive && typeof data?.count === "number") {
            setUserCount(data.count);
            writeJSON(USER_COUNT_CACHE_KEY, { v: data.count, ts: Date.now() });
          }
        }
      } catch {}
    })();

    // 3.5) Used drivers
    (async () => {
      try {
        if (driverCount == null) {
          const { data } = await api.get("/companies/drivers/count");
          if (alive && typeof data?.count === "number") {
            setDriverCount(data.count);
            writeJSON(DRIVER_COUNT_CACHE_KEY, { v: data.count, ts: Date.now() });
          }
        }
      } catch {}
    })();

    // 4) Current company → read plan + max_trucks + max_trailers + max_drivers + max_users + billing_cycle
    (async () => {
      try {
        if (
          plan == null ||
          maxTrucks == null ||
          maxTrailers == null ||
          maxDrivers == null ||
          maxUsers == null ||
          billingCycle == null
        ) {
          const { data } = await api.get(`/companies/current`);
          const p   = typeof data?.plan === "string" ? data.plan.toLowerCase() : null;
          const bc  = typeof data?.billing_cycle === "string" ? data.billing_cycle.toLowerCase() : null;
          const mt  = Number(data?.max_trucks);
          const mtr = Number(data?.max_trailers);
          const md  = Number(data?.max_drivers);
          const mu  = Number(data?.max_users);

          if (alive && p)  {
            setPlan(p);
            writeJSON(PLAN_CACHE_KEY, { v: p, ts: Date.now() });
          }
          if (alive && bc) {
            setBillingCycle(bc);
            writeJSON(BILLING_CYCLE_CACHE_KEY, { v: bc, ts: Date.now() });
          }
          if (alive && Number.isFinite(mt)) {
            setMaxTrucks(mt);
            writeJSON(MAX_TRUCKS_CACHE_KEY, { v: mt, ts: Date.now() });
          }
          if (alive && Number.isFinite(mtr)) {
            setMaxTrailers(mtr);
            writeJSON(MAX_TRAILERS_CACHE_KEY, { v: mtr, ts: Date.now() });
          }
          if (alive && Number.isFinite(md)) {
            setMaxDrivers(md);
            writeJSON(MAX_DRIVERS_CACHE_KEY, { v: md, ts: Date.now() });
          }
          if (alive && Number.isFinite(mu)) {
            setMaxUsers(mu);
            writeJSON(MAX_USERS_CACHE_KEY, { v: mu, ts: Date.now() });
          }
        }
      } catch {}
    })();

    // 5) Current period end (billing)
    (async () => {
      try {
        if (currentPeriodEnd == null) {
          const { data } = await api.get("/companies/current-period-end.raw");
          const cpe = data?.current_period_end ? String(data.current_period_end) : null;
          if (cpe) {
            setCurrentPeriodEnd(cpe);
            setPlanStatus(data?.plan_status ?? null);
            setAutoRenews(!!data?.auto_renews);
            setGraceDays(Number.isFinite(Number(data?.grace_days)) ? Number(data.grace_days) : null);
            writeJSON(CPE_CACHE_KEY, {
              v: cpe,
              ts: Date.now(),
              ps: data?.plan_status ?? null,
              ar: !!data?.auto_renews,
              gd: Number.isFinite(Number(data?.grace_days)) ? Number(data.grace_days) : null
            });
          }
        }
      } catch {}
    })();

    // 6) Real plan + prices
    (async () => {
      try {
        if (!planPrices?.plan || planPrices.monthly_price == null || planPrices.yearly_price == null) {
          const { data } = await api.get("/companies/plan/prices.raw");
          const v = {
            plan: (data?.plan || null),
            monthly_price: (data?.monthly_price ?? null),
            yearly_price:  (data?.yearly_price  ?? null),
          };
          setPlanPrices(v);
          writeJSON(PLAN_PRICES_CACHE_KEY, { v, ts: Date.now() });
        }
      } catch {}
    })();

    return () => { alive = false; };
  }, [truckCount, trailerCount, userCount, driverCount, plan, maxTrucks, maxTrailers, maxUsers]);

  // Keep your mock for other parts (trailers/drivers) until wired to API
  const summary = useMemo(
    () => ({
      plan: "Fleet",
      status: "active",
      renewalDate: "2025-09-27",
      price: { monthly: 99_00, yearly: 99_00 * 12 * 0.9 },
      currency: "eur",
      usage: {
        trucks:   { used: 18, limit: null }, // UI no longer relies on this
        trailers: { used: 7, limit: 15 },
        drivers: { used: 23, limit: 30 },
      },
      paymentMethod: { brand: "visa", last4: "4242", expMonth: 10, expYear: 2027 },
    }),
    []
  );

  const euro = (cents) =>
    (cents / 100).toLocaleString(undefined, { style: "currency", currency: summary.currency });

  // ---- Plan catalog (for comparison grid) ----
  const plans = useMemo(
    () => [
      {
        id: "starter",
        name: "Starter",
        priceMonthly: 39_00,
        priceYearly:  139_00,
        limits: { trucks: 3, trailers: 3, drivers: 3, users: 2 },
        features: ["Basic dashboards", "Email support", "CMR uploads: Limited"],
      },
      {
        id: "fleet",
        name: "Fleet",
        priceMonthly: 89_00,
        priceYearly:  890_00,
        limits: { trucks: 10, trailers: 10, drivers: 10, users: 3 },
        features: ["Priority support", "Advanced rules", "CMR uploads: Unlimited"],
      },
      {
        id: "fleetpro",
        name: "FleetPro",
        priceMonthly: 179_00,
        priceYearly:  1_790_00,
        limits: { trucks: 25, trailers: 25, drivers: 25, users: 5 },
        features: ["SLA", "Private boards", "Advanced analytics"],
      },
      {
        id: "corporate",
        name: "Corporate",
        priceMonthly: 349_00,
        priceYearly:  3_490_00,
        limits: { trucks: 50, trailers: 50, drivers: 50, users: 10 },
        features: ["SLA", "Private boards", "Advanced analytics"],
      },
      {
        id: "enterprise",
        name: "Enterprise",
        priceMonthly: null,
        priceYearly:  null,
        limits: { trucks: "Custom", trailers: "Custom", drivers: "Custom", users: "Custom" },
        features: ["Custom SLA", "SSO/SAML", "Dedicated support"],
        note: t("compare.contact_note", { defaultValue: "(to Negociate)" }),
      },
    ],
    [t]
  );

  const currentPlanId = useMemo(() => {
    const p = (plan || planPrices?.plan || "").toLowerCase().trim();
    return p || null;
  }, [plan, planPrices?.plan]);

  const currentCatalogPlan = useMemo(
    () => (currentPlanId ? plans.find(p => p.id === currentPlanId) : null),
    [plans, currentPlanId]
  );

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(); } catch { return "—"; }
  };

  const renewalText = useMemo(() => {
    if (!currentPeriodEnd) {
      return t("summary.renews_on", { date: new Date(summary.renewalDate).toLocaleDateString() });
    }
    const key = autoRenews ? "summary.renews_on" : "summary.ends_on";
    return t(key, { date: formatDate(currentPeriodEnd) });
  }, [currentPeriodEnd, autoRenews, summary.renewalDate, t]);

  // ---- Local billing details form state (save to API later) ----
  const [billing, setBilling] = useState({
    companyName: "",
    vatId: "",
    billingEmails: "",
    poReference: "",
  });

  const handleBillingChange = (k, v) => setBilling((p) => ({ ...p, [k]: v }));

  const handleChangePlan = (targetPlanId) => {
    // wire to API
    alert(t("alerts.change_plan", { plan: targetPlanId, cycle }));
  };

  const handleUpdatePayment = () => {
    alert(t("alerts.update_payment"));
  };

  const handleRequestBankTransfer = () => {
    alert(t("alerts.request_bank"));
  };

  const handleSaveBilling = (e) => {
    e.preventDefault();
    alert(t("alerts.billing_saved"));
  };

  const realMonthlyCents = toCents(planPrices?.monthly_price);
  const realYearlyCents  = toCents(planPrices?.yearly_price);

  const catalogMonthlyCents = currentCatalogPlan?.priceMonthly ?? null;
  const catalogYearlyCents  = currentCatalogPlan?.priceYearly  ?? null;

  const monthlyFromYearly = Number.isFinite(realYearlyCents) ? Math.round(realYearlyCents / 12) : null;
  const yearlyFromMonthly = Number.isFinite(realMonthlyCents) ? realMonthlyCents * 12 : null;

  const displayMonthlyCents = Number.isFinite(catalogMonthlyCents)
    ? catalogMonthlyCents
    : Number.isFinite(realMonthlyCents)
      ? realMonthlyCents
      : Number.isFinite(monthlyFromYearly)
        ? monthlyFromYearly
        : summary.price.monthly;

  const displayYearlyCents = Number.isFinite(catalogYearlyCents)
    ? catalogYearlyCents
    : Number.isFinite(realYearlyCents)
      ? realYearlyCents
      : Number.isFinite(yearlyFromMonthly)
        ? yearlyFromMonthly
        : Math.round(summary.price.yearly);

  const compareHeaderRef = useRef(null);

  const scrollToCompare = () => {
    if (billingCycle) setCycle(billingCycle);
    const el = compareHeaderRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const card = el.closest(".plans-card");
    card?.classList.add("compare-flash");
    setTimeout(() => card?.classList.remove("compare-flash"), 900);
  };

  return (
    <div className="settings-page plans">
      <h1>{t("title")}</h1>
      <p className="plans-subtitle">{t("subtitle")}</p>

      {/* ---- Plan Summary Card ---- */}
      <section className="plans-card plans-summary">
        <div className="plans-summary__top">
          <div>
            <div className="plans-summary__plan">
              {t("summary.current_plan", { plan: prettyPlan(plan || planPrices?.plan) })}
            </div>
            <div className="plans-summary__status">
              {t("summary.status")}{" "}
              <span className={`badge badge--${summary.status}`}>
                {t(`summary.badge.${summary.status}`, summary.status.replace("_", " "))}
              </span>
            </div>
          </div>

          {/* Billing cycle toggle */}
          <div className="plans-toggle">
            <button
              type="button"
              className={`toggle-btn ${cycle === "monthly" ? "is-active" : ""}`}
              onClick={() => setCycle("monthly")}
            >
              {t("toggle.monthly")}
            </button>
            <button
              type="button"
              className={`toggle-btn ${cycle === "yearly" ? "is-active" : ""}`}
              onClick={() => setCycle("yearly")}
            >
              {t("toggle.yearly")}
            </button>
          </div>
        </div>

        <div className="plans-summary__price">
          {!currentPlanId ? (
            <span className="price skeleton">—</span>
          ) : cycle === "monthly" ? (
            <>
              <span className="price">{euro(displayMonthlyCents)}</span>
              <span className="per">{t("price.per_month")}</span>
            </>
          ) : (
            <>
              <span className="price">{euro(displayYearlyCents)}</span>
              <span className="per">{t("price.per_year")}</span>
              <div className="subprice">
                {t("price.approx_per_month", { amount: euro(Math.round(displayYearlyCents / 12)) })}
              </div>
            </>
          )}
        </div>

        <div className="plans-summary__meta">{renewalText}</div>

        <div className="plans-summary__actions">
          <button className="btn btn--primary" onClick={scrollToCompare}>
            {t("actions.change_plan")}
          </button>
          <button className="btn" onClick={handleUpdatePayment}>
            {t("actions.update_payment")}
          </button>
          <button className="btn btn--ghost" onClick={() => alert(t("alerts.cancel_pause"))}>
            {t("actions.cancel_pause")}
          </button>
        </div>
      </section>

      {/* ---- Usage ---- */}
      <section className="plans-card">
        <h2>{t("usage.title")}</h2>

        {/* Enquanto não tivermos todos os valores, mostramos só a bolinha de loading */}
        {!usageReady ? (
          <div className="plans-usage-loading">
            <div className="plans-usage-spinner" />
            <span>{t("usage.loading", "Loading usage…")}</span>
          </div>
        ) : (
          <>
            <UsageMeter
              label={t("usage.labels.trucks")}
              used={truckCount}
              limit={displayMaxTrucks}
            />
            <UsageMeter
              label={t("usage.labels.trailers")}
              used={trailerCount}
              limit={displayMaxTrailers}
            />
            <UsageMeter
              label={t("usage.labels.users")}
              used={userCount}
              limit={displayMaxUsers}
            />
            <UsageMeter
              label={t("usage.labels.drivers")}
              used={driverCount}
              limit={displayMaxDrivers}
            />

            <div className="plans-hint">{t("usage.need_more")}</div>
          </>
        )}
      </section>

      {/* ---- Plan Comparison ---- */}
      <section className="plans-card plans-compare">
        <div className="plans-grid__header" ref={compareHeaderRef}>
          <h2>{t("compare.title")}</h2>
          <div className="plans-toggle small">
            <button
              type="button"
              className={`toggle-btn ${cycle === "monthly" ? "is-active" : ""}`}
              onClick={() => setCycle("monthly")}
            >
              {t("toggle.monthly")}
            </button>
            <button
              type="button"
              className={`toggle-btn ${cycle === "yearly" ? "is-active" : ""}`}
              onClick={() => setCycle("yearly")}
            >
              {t("toggle.yearly")}
            </button>
          </div>
        </div>

        <div className="plans-grid">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`plan-tile ${currentPlanId === p.id && billingCycle === cycle ? "is-current" : ""}`}
            >
              <div className="plan-tile__name">
                {p.name} {p.note ? <span className="plan-note">{p.note}</span> : null}
                {currentPlanId === p.id && billingCycle ? (
                  <span className="plan-note">({t(`toggle.${billingCycle}`)})</span>
                ) : null}
              </div>

              <div className="plan-tile__price">
                {p.priceMonthly === null ? (
                  <span>{t("compare.contact_us")}</span>
                ) : cycle === "monthly" ? (
                  <>
                    <span className="price">{euro(p.priceMonthly)}</span>
                    <span className="per">{t("price.per_month")}</span>
                  </>
                ) : (
                  <>
                    <span className="price">{euro(p.priceYearly)}</span>
                    <span className="per">{t("price.per_year")}</span>
                    <div className="subprice">
                      {t("price.approx_per_month", { amount: euro(Math.round(p.priceYearly / 12)) })}
                    </div>
                  </>
                )}
              </div>

              <ul className="plan-tile__limits">
                <li>
                  {typeof p.limits.trucks === "string" ? (
                    <strong>{t("compare.custom")}</strong>
                  ) : (
                    <>
                      {t("compare.up_to", {
                        count: p.limits.trucks,
                        thing: t("compare.things.trucks"),
                      })}
                    </>
                  )}
                </li>
                <li>
                  {typeof p.limits.trailers === "string" ? (
                    <strong>{t("compare.custom")}</strong>
                  ) : (
                    <>
                      {t("compare.up_to", {
                        count: p.limits.trailers,
                        thing: t("compare.things.trailers"),
                      })}
                    </>
                  )}
                </li>
                <li>
                  {typeof p.limits.drivers === "string" ? (
                    <strong>{t("compare.custom")}</strong>
                  ) : (
                    <>
                      {t("compare.up_to", {
                        count: p.limits.drivers,
                        thing: t("compare.things.drivers"),
                      })}
                    </>
                  )}
                </li>
                <li>
                  {typeof p.limits.users === "string" ? (
                    <strong>{t("compare.custom")}</strong>
                  ) : (
                    <>
                      {t("compare.up_to", {
                        count: p.limits.users,
                        thing: t("compare.things.users"),
                      })}
                    </>
                  )}
                </li>
              </ul>

              <ul className="plan-tile__features">
                {p.features?.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>

              <div className="plan-tile__actions">
                {p.id === "enterprise" ? (
                  <button className="btn btn--primary" onClick={() => alert(t("compare.talk_to_sales"))}>
                    {t("compare.talk_to_sales")}
                  </button>
                ) : (
                  <button
                    className="btn btn--primary"
                    onClick={() => handleChangePlan(p.id)}
                    disabled={currentPlanId === p.id && billingCycle === cycle}
                  >
                    {currentPlanId === p.id && billingCycle === cycle
                      ? t("compare.current_plan")
                      : t("compare.choose_plan")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Payment Methods ---- */}
      <section className="plans-card">
        <h2>{t("payment.title")}</h2>
        <div className="payment-method">
          <div>
            <div className="pm-title">{t("payment.default_method")}</div>
            <div className="pm-line">
              {summary.paymentMethod.brand?.toUpperCase()} •••• {summary.paymentMethod.last4} —{" "}
              {t("payment.exp", {
                month: String(summary.paymentMethod.expMonth).padStart(2, "0"),
                year: summary.paymentMethod.expYear,
              })}
            </div>
          </div>
          <div className="pm-actions">
            <button className="btn" onClick={handleUpdatePayment}>{t("payment.replace_card")}</button>
            <button className="btn btn--ghost" onClick={() => alert(t("alerts.add_backup"))}>
              {t("payment.add_backup")}
            </button>
          </div>
        </div>

        <div className="bank-transfer">
          <div className="bank-transfer__title">{t("bank_transfer.title")}</div>
          <p>{t("bank_transfer.desc")}</p>
          <button className="btn" onClick={handleRequestBankTransfer}>{t("bank_transfer.cta")}</button>
        </div>
      </section>

      {/* ---- Billing Details ---- */}
      <section className="plans-card">
        <h2>{t("billing.title")}</h2>
        <form className="billing-form" onSubmit={handleSaveBilling}>
          <div className="bf-row">
            <label>{t("billing.company_name")}</label>
            <input
              type="text"
              value={billing.companyName}
              onChange={(e) => handleBillingChange("companyName", e.target.value)}
              placeholder={t("billing.placeholders.company")}
              required
            />
          </div>
          <div className="bf-row">
            <label>{t("billing.vat_id")}</label>
            <input
              type="text"
              value={billing.vatId}
              onChange={(e) => handleBillingChange("vatId", e.target.value)}
              placeholder={t("billing.placeholders.vat")}
            />
          </div>
          <div className="bf-row">
            <label>{t("billing.billing_emails")}</label>
            <input
              type="text"
              value={billing.billingEmails}
              onChange={(e) => handleBillingChange("billingEmails", e.target.value)}
              placeholder={t("billing.placeholders.emails")}
            />
          </div>
          <div className="bf-row">
            <label>{t("billing.po_reference")}</label>
            <input
              type="text"
              value={billing.poReference}
              onChange={(e) => handleBillingChange("poReference", e.target.value)}
              placeholder={t("billing.placeholders.po")}
            />
          </div>
          <div className="bf-actions">
            <button className="btn btn--primary" type="submit">{t("billing.save")}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

// Tiny JSON localStorage helpers
const readJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

const toCents = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const num = Number(n);
  return Number.isFinite(num) ? Math.round(num * 100) : null;
};

const prettyPlan = (p) => ({
  starter: "Starter",
  fleet: "Fleet",
  fleetpro: "FleetPro",
  corporate: "Corporate",
  enterprise: "Enterprise",
}[String(p || "").toLowerCase()] || (p ? String(p)[0].toUpperCase() + String(p).slice(1) : "—"));

// Caching (snappy, adapts quickly if changed elsewhere)
const TRUCK_COUNT_CACHE_KEY    = "stats.truckCount";
const TRUCK_COUNT_TTL_MS       = 20_000;

const TRAILER_COUNT_CACHE_KEY  = "stats.trailerCount";
const TRAILER_COUNT_TTL_MS     = 20_000;

const USER_COUNT_CACHE_KEY     = "stats.userCount";
const USER_COUNT_TTL_MS        = 20_000;

// 👉 INSERIR LOGO APÓS estas duas linhas:
const DRIVER_COUNT_CACHE_KEY   = "stats.driverCount";
const DRIVER_COUNT_TTL_MS      = 20_000;

const MAX_TRUCKS_CACHE_KEY     = "stats.maxTrucks";
const MAX_TRUCKS_TTL_MS        = 5 * 60_000;

const MAX_TRAILERS_CACHE_KEY   = "stats.maxTrailers";
const MAX_TRAILERS_TTL_MS      = 5 * 60_000;

// 👉 INSERIR LOGO APÓS estas duas linhas:
const MAX_DRIVERS_CACHE_KEY    = "stats.maxDrivers";
const MAX_DRIVERS_TTL_MS       = 5 * 60_000;

const MAX_USERS_CACHE_KEY      = "stats.maxUsers";
const MAX_USERS_TTL_MS         = 5 * 60_000;

// Real plan prices (from /companies/plan/prices.raw)
const PLAN_PRICES_CACHE_KEY = "stats.planPrices";
const PLAN_PRICES_TTL_MS    = 5 * 60_000; // 5 min

// --- Plan → default max_trucks/max_trailers mapping ---
const PLAN_LIMITS = {
  starter: 3,
  fleet: 10,
  fleetpro: 25,
  corporate: 50,
  enterprise: null,
};

// --- Plan → default max_users mapping ---
const PLAN_LIMITS_USERS = {
  starter: 2,
  fleet: 3,
  fleetpro: 5,
  corporate: 10,
  enterprise: null,
};

// --- Plan cache ---
const PLAN_CACHE_KEY = "stats.plan";
const PLAN_TTL_MS    = 5 * 60_000;

export function invalidatePlanCache() {
  try { localStorage.removeItem(PLAN_CACHE_KEY); } catch {}
}

export function invalidateTruckCountCache()   { try { localStorage.removeItem(TRUCK_COUNT_CACHE_KEY); } catch {} }
export function invalidateTrailerCountCache() { try { localStorage.removeItem(TRAILER_COUNT_CACHE_KEY); } catch {} }
export function invalidateUserCountCache()    { try { localStorage.removeItem(USER_COUNT_CACHE_KEY); } catch {} }
export function invalidateDriverCountCache()  { try { localStorage.removeItem(DRIVER_COUNT_CACHE_KEY); } catch {} }

export function invalidateMaxTrucksCache()    { try { localStorage.removeItem(MAX_TRUCKS_CACHE_KEY); } catch {} }
export function invalidateMaxTrailersCache()  { try { localStorage.removeItem(MAX_TRAILERS_CACHE_KEY); } catch {} }
export function invalidateMaxUsersCache()     { try { localStorage.removeItem(MAX_USERS_CACHE_KEY); } catch {} }

/* ---------- Small helpers (inline components) ---------- */
function UsageMeter({ label, used, limit }) {
  const showNumbers = Number.isFinite(used) && Number.isFinite(limit);
  const pct = showNumbers ? Math.min(100, Math.round((used / Math.max(limit, 1)) * 100)) : 0;

  return (
    <div className="meter">
      <div className="meter__top">
        <span>{label}</span>
        <span>{showNumbers ? `${used} / ${limit}` : "— / —"}</span>
      </div>
      <div className="meter__bar">
        <div className="meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
